/**
 * Plateforme multi événement e.labriquerie.net, (c) Mikaël FOURNY.
 * src/utils/titreEcranDynamique.ts
 *
 * Rôle : le nom PRÉCIS d'un écran de fiche (une entreprise, un atelier, une
 *   page de contenu), connu seulement après la réponse du serveur.
 *
 * Pourquoi (référencement, 2026-10-08) :
 *   Le serveur (api/_lib/seo.php) écrit désormais le titre de chaque page
 *   AVANT le JavaScript : « Entreprise X (Salon) » pour une fiche
 *   d'exposant. Google lit ce titre, puis exécute l'application, qui le
 *   remplaçait par le titre de catégorie de titresDeRoute.ts (« Fiche de
 *   l'exposant (Salon) »). Deux titres pour une même adresse, et le moins
 *   précis gagnait. L'écran de fiche déclare donc son nom ici, et
 *   DocumentBranding le préfère au titre de catégorie, pour CETTE adresse
 *   seulement : la table des routes reste le plancher garanti partout.
 *
 * Petit magasin externe (useSyncExternalStore) plutôt qu'un contexte : la
 * fiche et DocumentBranding ne sont pas dans la même branche de l'arbre, et
 * l'ordre d'exécution de leurs effets ne doit rien décider.
 *
 * Mikael - chef de projet - 2026-10-08
 */

import { useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';

const titres = new Map<string, string>();
const abonnes = new Set<() => void>();

function prevenir(): void {
  for (const f of abonnes) f();
}

function sAbonner(f: () => void): () => void {
  abonnes.add(f);
  return () => {
    abonnes.delete(f);
  };
}

/** Chemin sans barre finale, comme la comparaison de routes. */
function cle(chemin: string): string {
  return chemin.length > 1 ? chemin.replace(/\/+$/, '') || '/' : chemin;
}

/** Pose (ou retire, avec null) le nom précis de l'écran affiché à `chemin`. */
export function poserTitreEcran(chemin: string, titre: string | null): void {
  const k = cle(chemin);
  const t = (titre ?? '').trim();
  if (t === '') {
    if (titres.delete(k)) prevenir();
    return;
  }
  if (titres.get(k) === t) return;
  titres.set(k, t);
  prevenir();
}

/** Nom précis déclaré pour `chemin`, ou null (titre de catégorie). */
export function titreEcranPour(chemin: string): string | null {
  return titres.get(cle(chemin)) ?? null;
}

/** Abonnement de DocumentBranding au nom précis de l'adresse courante. */
export function useTitreEcranDynamique(chemin: string): string | null {
  return useSyncExternalStore(
    sAbonner,
    () => titreEcranPour(chemin),
    () => titreEcranPour(chemin),
  );
}

/**
 * Pour un écran de fiche : déclare son nom (une fois chargé) pour l'adresse
 * courante, et le retire en quittant l'écran.
 */
export function useTitreEcran(titre: string | null | undefined): void {
  const { pathname } = useLocation();
  useEffect(() => {
    poserTitreEcran(pathname, titre ?? null);
    return () => poserTitreEcran(pathname, null);
  }, [pathname, titre]);
}
